import { supabase } from './supabase';
import { RealtimeChannel } from '@supabase/supabase-js';

export type RealtimeCallback<T = any> = (payload: T) => void;

class RealtimeService {
  private channels: Map<string, RealtimeChannel> = new Map();
  private pollingIntervals: Map<string, NodeJS.Timeout> = new Map();

  // Subscribe to conversation messages using broadcast events (no table replication needed)
  subscribeToConversation(
    conversationId: string,
    onNewMessage: RealtimeCallback,
    onMessageUpdate: RealtimeCallback,
    onMessageDelete: RealtimeCallback
  ): () => void {
    const channelName = `conversation:${conversationId}`;
    
    // Remove existing channel if it exists
    this.unsubscribeFromChannel(channelName);

    console.log(`🔄 Subscribing to conversation: ${conversationId}`);

    const channel = supabase
      .channel(channelName)
      .on('broadcast', { event: 'new_message' }, (payload) => {
        console.log('📨 New message broadcast received:', payload);
        onNewMessage(payload.payload);
      })
      .on('broadcast', { event: 'message_updated' }, (payload) => {
        console.log('📝 Message update broadcast received:', payload);
        onMessageUpdate(payload.payload);
      })
      .on('broadcast', { event: 'message_deleted' }, (payload) => {
        console.log('🗑️ Message delete broadcast received:', payload);
        onMessageDelete(payload.payload.messageId);
      })
      .subscribe((status) => {
        console.log(`📡 Conversation ${conversationId} subscription status:`, status);
        
        if (status === 'SUBSCRIBED') {
          console.log('✅ Successfully subscribed to conversation broadcasts');
          // Start polling for missed messages as backup
          this.startMessagePolling(conversationId, onNewMessage);
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Channel subscription error');
        } else if (status === 'TIMED_OUT') {
          console.error('⏰ Channel subscription timed out');
        }
      });

    this.channels.set(channelName, channel);

    // Return unsubscribe function
    return () => {
      this.unsubscribeFromChannel(channelName);
      this.stopMessagePolling(conversationId);
    };
  }

  // Start polling for new messages as backup when replication is not available
  private startMessagePolling(conversationId: string, onNewMessage: RealtimeCallback): void {
    const pollKey = `poll:${conversationId}`;
    
    // Clear existing polling
    this.stopMessagePolling(conversationId);
    
    let lastMessageTime = new Date().toISOString();
    
    const pollInterval = setInterval(async () => {
      try {
        const { data, error } = await supabase
          .from('messages')
          .select(`
            *,
            sender:profiles!messages_sender_id_fkey (
              id,
              username,
              full_name,
              avatar_url
            )
          `)
          .eq('conversation_id', conversationId)
          .gt('created_at', lastMessageTime)
          .order('created_at', { ascending: true });

        if (error) {
          console.error('❌ Polling error:', error);
          return;
        }

        if (data && data.length > 0) {
          console.log(`📥 Polling found ${data.length} new messages`);
          
          data.forEach(message => {
            onNewMessage(this.transformMessage(message));
          });
          
          // Update last message time
          lastMessageTime = data[data.length - 1].created_at;
        }
      } catch (error) {
        console.error('❌ Message polling error:', error);
      }
    }, 2000); // Poll every 2 seconds

    this.pollingIntervals.set(pollKey, pollInterval);
    console.log(`🔄 Started message polling for conversation: ${conversationId}`);
  }

  private stopMessagePolling(conversationId: string): void {
    const pollKey = `poll:${conversationId}`;
    const interval = this.pollingIntervals.get(pollKey);
    
    if (interval) {
      clearInterval(interval);
      this.pollingIntervals.delete(pollKey);
      console.log(`⏹️ Stopped message polling for conversation: ${conversationId}`);
    }
  }

  // Broadcast new message to other users in the conversation
  async broadcastNewMessage(conversationId: string, message: any): Promise<void> {
    const channelName = `conversation:${conversationId}`;
    const channel = this.channels.get(channelName);
    
    if (channel) {
      console.log('📤 Broadcasting new message:', message);
      await channel.send({
        type: 'broadcast',
        event: 'new_message',
        payload: message
      });
    } else {
      console.warn('⚠️ No channel found for broadcasting message');
    }
  }

  // Subscribe to user's conversations list using polling
  subscribeToUserConversations(
    userId: string,
    onConversationUpdate: RealtimeCallback
  ): () => void {
    const channelName = `user-conversations:${userId}`;
    
    this.unsubscribeFromChannel(channelName);

    console.log(`🔄 Subscribing to user conversations: ${userId}`);

    const channel = supabase
      .channel(channelName)
      .on('broadcast', { event: 'conversation_updated' }, (payload) => {
        console.log('💬 Conversation update broadcast:', payload);
        onConversationUpdate(payload.payload);
      })
      .subscribe((status) => {
        console.log(`📡 User conversations subscription status:`, status);
        
        if (status === 'SUBSCRIBED') {
          // Start polling for conversation updates
          this.startConversationPolling(userId, onConversationUpdate);
        }
      });

    this.channels.set(channelName, channel);

    return () => {
      this.unsubscribeFromChannel(channelName);
      this.stopConversationPolling(userId);
    };
  }

  private startConversationPolling(userId: string, onConversationUpdate: RealtimeCallback): void {
    const pollKey = `conv-poll:${userId}`;
    
    this.stopConversationPolling(userId);
    
    let lastUpdateTime = new Date().toISOString();
    
    const pollInterval = setInterval(async () => {
      try {
        const { data, error } = await supabase
          .from('conversations')
          .select('id, last_message_at, updated_at')
          .contains('participant_ids', [userId])
          .gt('last_message_at', lastUpdateTime)
          .order('last_message_at', { ascending: false });

        if (error) {
          console.error('❌ Conversation polling error:', error);
          return;
        }

        if (data && data.length > 0) {
          console.log(`📥 Found ${data.length} updated conversations`);
          
          data.forEach(conv => {
            onConversationUpdate({
              event: 'conversation_updated',
              conversation_id: conv.id,
              last_message_at: conv.last_message_at
            });
          });
          
          lastUpdateTime = data[0].last_message_at;
        }
      } catch (error) {
        console.error('❌ Conversation polling error:', error);
      }
    }, 5000); // Poll every 5 seconds

    this.pollingIntervals.set(pollKey, pollInterval);
  }

  private stopConversationPolling(userId: string): void {
    const pollKey = `conv-poll:${userId}`;
    const interval = this.pollingIntervals.get(pollKey);
    
    if (interval) {
      clearInterval(interval);
      this.pollingIntervals.delete(pollKey);
    }
  }

  // Subscribe to typing indicators (works without replication)
  subscribeToTyping(
    conversationId: string,
    onTypingUpdate: RealtimeCallback
  ): () => void {
    const channelName = `typing:${conversationId}`;
    
    this.unsubscribeFromChannel(channelName);

    console.log(`⌨️ Subscribing to typing indicators: ${conversationId}`);

    const channel = supabase
      .channel(channelName)
      .on('broadcast', { event: 'typing' }, (payload) => {
        console.log('⌨️ Typing update:', payload);
        onTypingUpdate(payload);
      })
      .subscribe((status) => {
        console.log(`⌨️ Typing subscription status:`, status);
      });

    this.channels.set(channelName, channel);

    return () => this.unsubscribeFromChannel(channelName);
  }

  // Send typing indicator
  sendTypingIndicator(conversationId: string, userId: string, isTyping: boolean): void {
    const channelName = `typing:${conversationId}`;
    const channel = this.channels.get(channelName);
    
    if (channel) {
      console.log(`⌨️ Sending typing indicator: ${isTyping ? 'typing' : 'stopped typing'}`);
      channel.send({
        type: 'broadcast',
        event: 'typing',
        payload: {
          user_id: userId,
          is_typing: isTyping,
          timestamp: new Date().toISOString()
        }
      });
    } else {
      console.warn('⚠️ Typing channel not found');
    }
  }

  // Subscribe to online presence (works without replication)
  subscribeToPresence(
    conversationId: string,
    userId: string,
    userInfo: { username: string; avatar_url?: string },
    onPresenceUpdate: RealtimeCallback
  ): () => void {
    const channelName = `presence:${conversationId}`;
    
    this.unsubscribeFromChannel(channelName);

    console.log(`👥 Subscribing to presence: ${conversationId}`);

    const channel = supabase
      .channel(channelName)
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        console.log('👥 Presence sync:', state);
        onPresenceUpdate(state);
      })
      .on('presence', { event: 'join' }, ({ key, newPresences }) => {
        console.log('👥 User joined:', { key, newPresences });
        onPresenceUpdate({ event: 'join', key, presences: newPresences });
      })
      .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
        console.log('👥 User left:', { key, leftPresences });
        onPresenceUpdate({ event: 'leave', key, presences: leftPresences });
      })
      .subscribe(async (status) => {
        console.log(`👥 Presence subscription status:`, status);
        
        if (status === 'SUBSCRIBED') {
          console.log('👥 Tracking user presence...');
          await channel.track({
            user_id: userId,
            username: userInfo.username,
            avatar_url: userInfo.avatar_url,
            online_at: new Date().toISOString(),
          });
        }
      });

    this.channels.set(channelName, channel);

    return () => this.unsubscribeFromChannel(channelName);
  }

  // Subscribe to message read status using broadcasts
  subscribeToReadStatus(
    conversationId: string,
    onReadStatusUpdate: RealtimeCallback
  ): () => void {
    const channelName = `read-status:${conversationId}`;
    
    this.unsubscribeFromChannel(channelName);

    console.log(`📖 Subscribing to read status: ${conversationId}`);

    const channel = supabase
      .channel(channelName)
      .on('broadcast', { event: 'message_read' }, (payload) => {
        console.log('📖 Read status update:', payload);
        onReadStatusUpdate(payload);
      })
      .subscribe((status) => {
        console.log(`📖 Read status subscription status:`, status);
      });

    this.channels.set(channelName, channel);

    return () => this.unsubscribeFromChannel(channelName);
  }

  // Send read status
  sendReadStatus(conversationId: string, userId: string, messageId: string): void {
    const channelName = `read-status:${conversationId}`;
    const channel = this.channels.get(channelName);
    
    if (channel) {
      console.log(`📖 Sending read status for message: ${messageId}`);
      channel.send({
        type: 'broadcast',
        event: 'message_read',
        payload: {
          user_id: userId,
          message_id: messageId,
          read_at: new Date().toISOString()
        }
      });
    }
  }

  // Test connection
  async testConnection(): Promise<boolean> {
    try {
      console.log('🧪 Testing Supabase connection...');
      
      const { data, error } = await supabase
        .from('profiles')
        .select('id')
        .limit(1);

      if (error) {
        console.error('❌ Connection test failed:', error);
        return false;
      }

      console.log('✅ Connection test successful');
      return true;
    } catch (error) {
      console.error('❌ Connection test error:', error);
      return false;
    }
  }

  // Get connection status
  getConnectionStatus(): string {
    const socket = (supabase as any).realtime?.socket;
    if (!socket) return 'No socket';
    
    return socket.readyState === 1 ? 'Connected' : 'Disconnected';
  }

  // Force reconnect
  async forceReconnect(): Promise<void> {
    console.log('🔄 Forcing reconnection...');
    
    // Stop all polling
    this.pollingIntervals.forEach((interval) => {
      clearInterval(interval);
    });
    this.pollingIntervals.clear();
    
    // Unsubscribe from all channels
    this.unsubscribeAll();
    
    // Disconnect and reconnect
    const realtime = (supabase as any).realtime;
    if (realtime) {
      realtime.disconnect();
      await new Promise(resolve => setTimeout(resolve, 1000));
      realtime.connect();
    }
  }

  // Unsubscribe from a specific channel
  private unsubscribeFromChannel(channelName: string): void {
    const channel = this.channels.get(channelName);
    if (channel) {
      console.log(`🔌 Unsubscribing from channel: ${channelName}`);
      supabase.removeChannel(channel);
      this.channels.delete(channelName);
    }
  }

  // Unsubscribe from all channels
  unsubscribeAll(): void {
    console.log('🔌 Unsubscribing from all channels...');
    this.channels.forEach((channel, channelName) => {
      console.log(`🔌 Removing channel: ${channelName}`);
      supabase.removeChannel(channel);
    });
    this.channels.clear();
    
    // Stop all polling
    this.pollingIntervals.forEach((interval) => {
      clearInterval(interval);
    });
    this.pollingIntervals.clear();
  }

  // Get active channels
  getActiveChannels(): string[] {
    return Array.from(this.channels.keys());
  }

  // Transform message data
  private transformMessage(data: any) {
    return {
      id: data.id,
      conversation_id: data.conversation_id,
      sender_id: data.sender_id,
      sender: {
        id: data.sender?.id || data.sender_id || '',
        username: data.sender?.username || 'unknown',
        displayName: data.sender?.full_name || data.sender?.username || 'Unknown User',
        profileImage: data.sender?.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
        bio: '',
        followerCount: 0,
        followingCount: 0,
        postCount: 0,
      },
      content: data.content,
      message_type: data.message_type,
      media_url: data.media_url,
      shared_post_id: data.shared_post_id,
      shared_post: data.shared_post ? {
        id: data.shared_post.id,
        caption: data.shared_post.caption,
        media: data.shared_post.post_media,
        user: {
          username: data.shared_post.profiles?.username || 'unknown',
        }
      } : undefined,
      created_at: data.created_at,
      updated_at: data.updated_at,
      is_read: data.is_read,
      is_deleted: data.is_deleted,
    };
  }
}

export const realtimeService = new RealtimeService();